import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  useTheme,
  Divider,
  Fab,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarIcon,
  PersonOutline as ClientUsersIcon,
  MenuOpen as MenuOpenIcon,
  Merge as MergeIcon,
  MarkEmailRead as InvitationsIcon,
  Email as EmailIcon,
  NewReleases as NewReleasesIcon,
  Help as HelpIcon,
  ContactMail as ContactMailIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Groups as GroupsIcon,
  History as HistoryIcon,
  CloudQueue as CloudIcon,
  Public as PublicIcon,
  EventNote as EventNoteIcon,
  LocalOffer as LabelIcon,
  QrCode2 as QrCodeIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
  ForwardToInbox as ForwardToInboxIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useIsRouting } from '../hooks/useIsRouting';
import apiService from '../services/apiService';

interface SidebarProps {
  drawerWidth?: number;
  onToggle?: (open: boolean) => void;
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path?: string;
  description?: string;
  submenu?: MenuItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ onToggle }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isRouting = useIsRouting();
  const [open, setOpen] = useState(true);
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const [version, setVersion] = useState<string | null>(null);
  const auth = useUser();
  const { selectedClientId, isAdmin, isToolsUser, isGroupAdmin, isICRole, user, viewMode, clients } = useUser();

  // React to view mode changes from UserContext
  useEffect(() => {
    console.log('[Sidebar] View mode changed:', viewMode);
  }, [viewMode]);

  // Auto-expand Admin submenu if on an admin page
  useEffect(() => {
    if (location.pathname.startsWith('/app/admin/')) {
      setOpenSubMenu('Admin');
    }
  }, [location.pathname]);

  // Fetch version for system admins
  useEffect(() => {
    const fetchVersion = async () => {
      const adminCheck = isAdmin();
      const userIsAdmin = user?.is_admin || false;
      console.log('[Sidebar] ===== VERSION FETCH DEBUG =====');
      console.log('[Sidebar] isAdmin() check:', adminCheck);
      console.log('[Sidebar] user.is_admin:', userIsAdmin);
      console.log('[Sidebar] user object:', user);
      console.log('[Sidebar] System believes user is admin:', adminCheck);

      if (!adminCheck) {
        console.log('[Sidebar] ❌ User is NOT admin, skipping version fetch');
        setVersion(null);
        return;
      }

      console.log('[Sidebar] ✅ User IS admin, fetching version...');

      try {
        const response = await apiService.get<{ version: string | null; has_pending_changes: boolean }>('/api/version');
        console.log('[Sidebar] Version API full response:', response);
        console.log('[Sidebar] Version API response.data:', response.data);
        console.log('[Sidebar] Version API response.data.version:', response.data?.version);
        console.log('[Sidebar] Version API response.data type:', typeof response.data);

        // Handle both direct response and nested data
        const versionData = response.data?.version !== undefined ? response.data : (response as any).data?.data || response.data;
        const versionValue = versionData?.version;

        console.log('[Sidebar] Extracted version:', versionValue);

        if (versionValue &&
            typeof versionValue === 'string' &&
            versionValue.toLowerCase() !== 'unknown' &&
            versionValue.toLowerCase() !== 'null' &&
            versionValue.trim() !== '') {
          setVersion(versionValue);
          console.log('[Sidebar] ✅ Set version:', versionValue);
        } else {
          console.log('[Sidebar] ❌ Version invalid or empty:', versionValue);
          setVersion(null);
        }
      } catch (error) {
        // Log error for debugging
        console.error('[Sidebar] ❌ Failed to fetch version:', error);
        setVersion(null);
      }
    };

    fetchVersion();
  }, [isAdmin, user]);

  const handleToggle = () => {
    const newOpen = !open;
    setOpen(newOpen);
    onToggle?.(newOpen);
  };

  // Function to check if a menu item is active
  const isActive = (path: string | undefined) => {
    if (!path) return false;

    // Dashboard special case - only highlight when exactly on dashboard
    if (path === '/app') {
      return location.pathname === '/app';
    }

    // Analytics special case - check for both overview and email performance pages
    if (path === '/app/analytics') {
      return location.pathname === '/app/analytics' ||
             location.pathname === '/app/analytics/overview' ||
             location.pathname === '/app/analytics/email-performance' ||
             location.pathname.startsWith('/app/analytics/email-performance/');
    }

    // For all other menu items, check if the current path starts with the menu path
    // This handles both exact matches and sub-paths
    if (location.pathname.startsWith(path)) {
      // Special case for analytics to ensure we don't match admin/analytics
      if (path === '/app/analytics' && location.pathname.startsWith('/app/admin/')) {
        return false;
      }
      return true;
    }

    return false;
  };

  // Check if a submenu item is active
  const isSubmenuActive = (path: string | undefined) => {
    if (!path) return false;

    // Exact match
    if (location.pathname === path) return true;

    // Special cases for admin pages
    if (path === '/admin/client-users' && location.pathname.startsWith('/admin/client-users')) {
      return true;
    }

    return false;
  };

  // Use navigate with a default no-op function if not in routing context
  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // Admin section organized into 4 clusters with subheadings.
  // Renderer below walks adminGroups (flatten via adminGroups.flatMap(g => g.items)
  // when a flat list is needed, e.g. for the "any admin items?" gate).
  const adminGroups: { heading: string; items: MenuItem[] }[] = isAdmin()
    ? [
      {
        heading: 'Clients & Access',
        items: [
          {
            text: 'Clients',
            icon: <BusinessIcon />,
            path: '/app/admin/clients',
            description: 'Clients | Manage client configurations'
          },
          {
            text: 'Users',
            icon: <ClientUsersIcon />,
            path: '/app/admin/client-users',
            description: 'Users | Manage user accounts and permissions'
          },
          {
            text: 'Invitations',
            icon: <InvitationsIcon />,
            path: '/app/admin/invitations',
            description: 'Invitations | Manage user invitations'
          },
          {
            text: 'Groups',
            icon: <GroupsIcon />,
            path: '/app/admin/groups',
            description: 'Groups | Create and manage groups'
          },
        ],
      },
      {
        heading: 'Content Tools',
        items: [
          {
            text: 'Email Schedule',
            icon: <EventNoteIcon />,
            path: '/app/admin/email-schedule',
            description: 'Email Schedule | Plan and track upcoming client emails across a 3-month calendar'
          },
          {
            text: 'Mail Merge',
            icon: <MergeIcon />,
            path: '/app/admin/mail-merge',
            description: 'Mail Merge | Copy emails between Connect instances'
          },
        ],
      },
      {
        heading: 'Infrastructure',
        items: [
          {
            text: 'Connect Instances',
            icon: <CloudIcon />,
            path: '/app/admin/connect-instances',
            description: 'Connect Instances | Manage Mautic connect instances'
          },
          {
            text: 'Group Analytics',
            icon: <GroupsIcon />,
            path: '/app/admin/group-analytics',
            description: 'Group Analytics | View aggregated analytics across all clients in your group'
          },
          {
            text: 'DMARC Reports',
            icon: <ShieldIcon />,
            path: '/app/admin/dmarc',
            description: 'DMARC Reports | Email authentication report monitoring across managed sending domains'
          },
          {
            text: 'TLS-RPT',
            icon: <LockIcon />,
            path: '/app/admin/tlsrpt',
            description: 'TLS-RPT | Inbound TLS handshake reports per RFC 8460 — readiness signal for MTA-STS enforce mode'
          },
          {
            text: 'Forward Mail',
            icon: <ForwardToInboxIcon />,
            path: '/app/admin/forward-mail',
            description: 'Forward Mail | Send AS our SES-inbound-forwarder addresses (no-mailbox domains) + edit forwarder targets'
          },
        ],
      },
      {
        heading: 'System',
        items: [
          {
            text: 'Audit Trail',
            icon: <HistoryIcon />,
            path: '/app/admin/audit-trail',
            description: 'Audit Trail | View system change history and activity logs'
          },
          {
            text: 'Release Notes',
            icon: <NewReleasesIcon />,
            path: '/app/admin/release-notes',
            description: 'Release Notes | Manage application release notes'
          },
        ],
      },
    ]
    : [];

  // Flat list — used by the section-presence gate and by collapsed-mode rendering
  // where group headings don't appear.
  const adminMenuItems: MenuItem[] = adminGroups.flatMap((g) => g.items);

  // Tools section: gated on isToolsUser() (admins implicitly included).
  // Sits between the regular menu and the Admin section in the sidebar.
  const toolsItems: MenuItem[] = isToolsUser()
    ? [
        {
          text: 'QR Codes',
          icon: <QrCodeIcon />,
          path: '/app/admin/qrcodes',
          description: 'QR Codes | Generate branded QR codes with static or dynamic destinations'
        },
        {
          text: 'Domain Finder',
          icon: <PublicIcon />,
          path: '/app/admin/domain-finder',
          description: 'Domain Finder | Generate domain names with AI and check availability via Route53'
        },
      ]
    : [];

  // Define regular menu items (excluding Help)
  // Filter out Agency Settings for IC role users
  const regularMenuItems: MenuItem[] = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/app',
      description: 'Dashboard | Overview of your marketing activities'
    },
    {
      text: 'Contacts',
      icon: <ContactMailIcon />,
      path: '/app/contacts',
      description: 'Contacts | Manage your contact database'
    },
    {
      text: 'Emails',
      icon: <EmailIcon />,
      path: '/app/emails',
      description: 'Emails | Create and manage email campaigns'
    },
    {
      text: 'Calendar',
      icon: <CalendarIcon />,
      path: '/app/calendar',
      description: 'Calendar | Schedule and manage campaigns'
    },
    {
      text: 'Analytics',
      icon: <BarChartIcon />,
      path: '/app/analytics',
      description: 'Analytics | View campaign performance metrics'
    },
    {
      text: 'Tags',
      icon: <LabelIcon />,
      path: '/app/admin/tags',
      description: 'Tags | Manage tags and view analytics'
    },
    // Only show Agency Settings for non-IC role users
    ...(!isICRole() ? [{
      text: 'Agency Settings',
      icon: <SettingsIcon />,
      path: '/app/agency-settings',
      description: 'Agency Settings | Configure your agency settings'
    }] : [])
  ];

  // Define help menu item separately
  const helpMenuItem: MenuItem = {
    text: 'Help',
    icon: <HelpIcon />,
    path: '/app/help',
    description: 'Help | Get support and training resources'
  };

  // Define welcome menu item
  const welcomeMenuItem: MenuItem = {
    text: 'Welcome',
    icon: <NewReleasesIcon />,
    path: '/app/welcome',
    description: 'Welcome | Get started with the marketing portal'
  };

  // Combine menu items based on user state
  const menuItems: MenuItem[] = isAdmin()
    ? [...regularMenuItems] // Show regular menus for admin
    : clients?.length > 0
      ? regularMenuItems // Show user menu if clients exist
      : user?.is_tools_user
        ? [] // Tools-only users have no client context — Tools section renders below
        : [welcomeMenuItem]; // Show welcome menu if no clients

  // If not authenticated, don't render the sidebar
  if (!auth.isAuthenticated) {
    return null;
  }

  // Determine if we should show Welcome
  const showWelcome = !user?.is_admin && !user?.is_tools_user && !user?.client_assignments?.length;

  // Render submenu items
  const renderSubmenuItems = (items: MenuItem[]) => (
    <List sx={{ pl: 2 }}>
      {items.map((item) => renderMenuItem(item, true))}
    </List>
  );

  // Updated renderMenuItem to handle submenu items
  const renderMenuItem = (menu: MenuItem, isSubmenu: boolean = false) => (
    <Tooltip
      key={menu.text}
      title={!open ? menu.text : ''}
      placement="right"
      arrow
      enterDelay={400}
      enterNextDelay={200}
      sx={{
        '& .MuiTooltip-tooltip': {
          backgroundColor: theme.palette.grey[800],
          color: theme.palette.common.white,
          fontSize: '0.75rem',
          borderRadius: 1,
          boxShadow: theme.shadows[2],
        },
        '& .MuiTooltip-arrow': {
          color: theme.palette.grey[800],
        },
      }}
    >
      <ListItem
        onClick={() => {
          if (menu.submenu) {
            // Toggle submenu expansion
            setOpenSubMenu(openSubMenu === menu.text ? null : menu.text);
          } else if (menu.path) {
            handleNavigation(menu.path);
          }
        }}
        sx={{
          minHeight: 44,
          height: 44,
          py: 0,
          px: 0,
          mx: 0,
          transition: theme.transitions.create(['background-color', 'border-left'], {
            duration: theme.transitions.duration.shorter,
          }),
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.light, 0.1),
            transition: theme.transitions.create('background-color'),
          },
          cursor: 'pointer',
          backgroundColor: isActive(menu.path)
            ? alpha(theme.palette.common.white, 0.15)
            : 'transparent',
          borderLeft: isActive(menu.path) && !menu.path?.includes('/admin/')
            ? `3px solid ${theme.palette.common.white}`
            : '3px solid transparent',
          pl: isSubmenu ? 2 : (open ? 2.5 : 0),
          pr: open ? 2 : 0,
          display: 'flex',
          justifyContent: open ? 'flex-start' : 'center',
          ...(menu.path?.includes('/admin/') && isActive(menu.path) && {
            pl: isSubmenu ? 2 : (open ? 2.5 : 0),
            ml: 0,
            width: '100%',
          }),
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            width: 36,
            display: 'flex',
            justifyContent: 'center',
            color: isActive(menu.path)
              ? theme.palette.common.white
              : alpha(theme.palette.common.white, 0.8),
            alignItems: 'center',
            transition: theme.transitions.create('color'),
          }}
        >
          {menu.icon}
        </ListItemIcon>
        <ListItemText
          primary={menu.text}
          sx={{
            opacity: open ? 1 : 0,
            display: open ? 'block' : 'none',
            color: isActive(menu.path)
              ? theme.palette.common.white
              : alpha(theme.palette.common.white, 0.8),
            '& .MuiTypography-root': {
              fontSize: '0.9rem',
              fontWeight: isActive(menu.path) ? 500 : 400,
            },
            transition: theme.transitions.create(['opacity', 'color']),
          }}
        />
      </ListItem>
    </Tooltip>
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: 64,
        flexShrink: 0,
        position: 'fixed',
        zIndex: 1000,
        '& .MuiDrawer-paper': {
          width: open ? 240 : 64,
          boxSizing: 'border-box',
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRight: 'none',
          transition: theme.transitions.create(['width', 'box-shadow'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          position: 'fixed',
          overflow: 'visible',
          boxShadow: theme.shadows[8],
          height: 'calc(100vh - 64px)',
          mt: '64px',
          p: 0,
          zIndex: 1000
        },
      }}
    >
      {/* Floating toggle button */}
      <Box sx={{
        position: 'absolute',
        right: -15,
        top: 16,
        zIndex: 1001,
        backgroundColor: 'white',
        borderRadius: '50%',
        boxShadow: theme.shadows[2],
        '&:hover': {
          backgroundColor: theme.palette.grey[100],
          boxShadow: theme.shadows[4],
        },
        width: 28,
        height: 28,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: theme.transitions.create(['box-shadow', 'background-color'], {
          duration: theme.transitions.duration.shorter,
        }),
      }}>
        <IconButton
          onClick={handleToggle}
          sx={{
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: 'transparent'
            },
            padding: '4px',
            zIndex: 1001
          }}
        >
          {open ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Main menu items */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Top menu items */}
        <List sx={{ pt: 2, flex: 1 }}>
          {menuItems.map((item) => renderMenuItem(item))}
        </List>

        {/* Bottom pinned items */}
        <Box>
          <Divider
            sx={{
              opacity: 0.2,
              my: 1.5,
              borderColor: theme.palette.primary.contrastText,
            }}
          />
          {toolsItems.length > 0 && (
            <>
              {open && (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    opacity: 0.7,
                    borderBottom: `1px solid ${alpha(theme.palette.primary.contrastText, 0.2)}`,
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      color: alpha(theme.palette.primary.contrastText, 0.8),
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Tools
                  </Typography>
                </Box>
              )}
              <Box>
                {toolsItems.map((item) => (
                  <Box key={item.text}>{renderMenuItem(item, true)}</Box>
                ))}
              </Box>
              {adminMenuItems.length > 0 && (
                <Divider
                  sx={{
                    opacity: 0.2,
                    my: 1.5,
                    borderColor: theme.palette.primary.contrastText,
                  }}
                />
              )}
            </>
          )}
          {adminMenuItems.length > 0 && (
            <>
              {/* Admin section header */}
              {open && (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    opacity: 0.7,
                    borderBottom: `1px solid ${alpha(theme.palette.primary.contrastText, 0.2)}`,
                    mb: 1
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      color: alpha(theme.palette.primary.contrastText, 0.8),
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase'
                    }}
                  >
                    Admin
                  </Typography>
                </Box>
              )}

              {/* Admin menu items, grouped into clusters with sub-headings.
                  In collapsed mode (open === false) we drop the sub-headings
                  and render a flat icon column — they'd be unreadable anyway. */}
              <Box>
                {adminGroups.map((group, gi) => (
                  <Box key={group.heading}>
                    {open && (
                      <Typography
                        variant="overline"
                        sx={{
                          display: 'block',
                          px: 2,
                          pt: gi === 0 ? 0.5 : 1.25,
                          pb: 0.25,
                          color: alpha(theme.palette.primary.contrastText, 0.55),
                          fontSize: '0.62rem',
                          fontWeight: 700,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          lineHeight: 1.4,
                        }}
                      >
                        {group.heading}
                      </Typography>
                    )}
                    {group.items.map((item) => (
                      <Box key={item.text}>
                        {renderMenuItem(item, true)}
                        {item.submenu && openSubMenu === item.text && (
                          <Box sx={{ pl: 2 }}>
                            {item.submenu.map((subItem) => renderMenuItem(subItem, true))}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </>
          )}
          {renderMenuItem(helpMenuItem)}

          {/* Version and Pending Changes for System Admins */}
          {(() => {
            const adminCheck = isAdmin();
            const shouldShow = adminCheck && version;
            console.log('[Sidebar] ===== VERSION DISPLAY CHECK =====');
            console.log('[Sidebar] isAdmin():', adminCheck);
            console.log('[Sidebar] version state:', version);
            console.log('[Sidebar] shouldShow:', shouldShow);
            console.log('[Sidebar] Release tag should be:', version);
            console.log('[Sidebar] System believes user is admin:', adminCheck);
            if (!adminCheck) {
              console.log('[Sidebar] ❌ NOT showing version - user is not admin');
            } else if (!version) {
              console.log('[Sidebar] ❌ NOT showing version - version is null/empty');
            } else {
              console.log('[Sidebar] ✅ SHOULD show version:', version);
            }
            return shouldShow;
          })() && (
            <>
              <Divider
                sx={{
                  opacity: 0.2,
                  my: 1.5,
                  borderColor: theme.palette.primary.contrastText,
                }}
              />
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.7rem',
                    color: alpha(theme.palette.primary.contrastText, 0.7),
                    lineHeight: 1.2,
                  }}
                >
                  Release: {version}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
